// The timezone picker's DATA and SEARCH. The DOM half lives in
// web/src/views/tzPicker.ts.
//
// Split this way on purpose. The curated list below is the one part of the
// original picker that cannot be regenerated from `Intl` — it was hand-written,
// it was lost in the migration (see docs/migration-gaps.md item 4), and it is
// the part most worth a test. `web/` has no test runner, this package does.
import { allTimezones } from './time.js';
/**
 * Common zones with friendly names, carried over verbatim from the PHP picker.
 *
 * The aliases are the point. Nobody thinks of their timezone as `America/Denver`
 * — they think "mountain", "MST", or the state they live in. Searching the raw
 * IANA list finds none of those.
 *
 * US-centric because the userbase is. Adding a region means adding entries here;
 * every zone remains reachable through the full list regardless.
 */
export const TZ_COMMON = [
    { id: 'America/New_York', label: 'Eastern Time (US & Canada)', hint: 'America/New_York', alias: 'eastern et est edt new york' },
    { id: 'America/Chicago', label: 'Central Time (US & Canada)', hint: 'America/Chicago', alias: 'central ct cst cdt chicago texas' },
    { id: 'America/Denver', label: 'Mountain Time (US & Canada)', hint: 'America/Denver', alias: 'mountain mt mst mdt denver colorado' },
    { id: 'America/Phoenix', label: 'Arizona (no DST)', hint: 'America/Phoenix', alias: 'arizona az phoenix' },
    { id: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', hint: 'America/Los_Angeles', alias: 'pacific pt pst pdt los angeles california seattle' },
    { id: 'America/Anchorage', label: 'Alaska Time', hint: 'America/Anchorage', alias: 'alaska akst akdt anchorage' },
    { id: 'Pacific/Honolulu', label: 'Hawaii Time', hint: 'Pacific/Honolulu', alias: 'hawaii hst honolulu' },
    { id: 'UTC', label: 'UTC', hint: 'Coordinated Universal Time', alias: 'utc gmt zulu universal' },
];
/** Default cap on rendered rows. The full IANA list is hundreds of entries. */
export const TZ_MAX_RESULTS = 60;
/**
 * Default zone for a form that does NOT offer an inherit row — currently the
 * signup form. Used only when the browser reports no usable zone of its own.
 *
 * ⚠ This deliberately duplicates the server's `SITE_TIMEZONE`, because the SPA
 * has no way to learn it (see the note on `tzDisplayLabel`). Keep the two in
 * step: if the site zone ever moves, this is the second place to change.
 *
 * Note that storing this explicitly is NOT the same as storing null. A null
 * personal zone follows `SITE_TIMEZONE` forever via `resolveViewerTz`; an
 * explicit `America/Denver` stays Mountain even if the site moves. For someone
 * who really is in Mountain time, staying put is the better answer.
 */
export const TZ_SIGNUP_DEFAULT = 'America/Denver';
/**
 * The full pool for a picker: the inherit row, then the curated zones, then every
 * remaining IANA zone.
 *
 * `inheritLabel` and `inheritHint` differ by form, and the difference matters: on
 * a group form the fallback is the SITE zone, on an event form it is the GROUP
 * zone. Those are separate chains — see the header of `time.ts`.
 *
 * Pass `null` to omit the inherit row entirely. That is right where "inherit"
 * is not a concept the person filling the form has any way to understand — a
 * stranger creating an account has never heard of the site zone, cannot be shown
 * what it is, and reads the row as a question with no answer. Everywhere the
 * blank genuinely means "follow the group/site", the row stays.
 */
export function tzPool(inheritLabel, inheritHint = '') {
    const common = [
        ...(inheritLabel === null
            ? []
            : [
                {
                    id: '',
                    label: inheritLabel,
                    hint: inheritHint,
                    alias: 'default site local group inherit',
                },
            ]),
        ...TZ_COMMON,
    ];
    const curated = new Set(TZ_COMMON.map((c) => c.id));
    const rest = allTimezones()
        .filter((z) => !curated.has(z))
        .map((z) => ({ id: z, label: z, hint: '', alias: z.replace(/[_/]/g, ' ') }));
    return [...common, ...rest];
}
/**
 * Substring search across label, hint, alias and id, preserving pool order so the
 * curated entries stay on top. An empty query returns the head of the pool.
 */
export function tzSearch(pool, query, max = TZ_MAX_RESULTS) {
    const q = query.trim().toLowerCase();
    const out = [];
    for (const zone of pool) {
        if (out.length >= max)
            break;
        if (!q) {
            out.push(zone);
            continue;
        }
        const haystack = `${zone.label} ${zone.hint} ${zone.alias} ${zone.id}`.toLowerCase();
        if (haystack.includes(q))
            out.push(zone);
    }
    return out;
}
/**
 * How a committed choice reads in the closed input.
 *
 * A curated zone is `label · hint`; the inherit row is `label (hint)`, or a bare
 * label when there is no hint to show. The SPA cannot learn the site zone, so the
 * hintless case is normal on group forms, not an edge case.
 */
export function tzDisplayLabel(pool, id) {
    const hit = pool.find((c) => c.id === id);
    if (!hit)
        return id;
    if (!hit.hint)
        return hit.label;
    return hit.id ? `${hit.label} · ${hit.hint}` : `${hit.label} (${hit.hint})`;
}
//# sourceMappingURL=timezones.js.map